<script setup lang="ts">
import { PhCaretDown } from '@phosphor-icons/vue'
import { DropdownMenuTrigger } from 'reka-ui'
import { computed, ref } from 'vue'

import { OwnButton } from '@/ui/OwnButton'
import { type OwnDropdownItem } from '@/ui/OwnDropdown/types'
import { OwnDropdownMenuV2, OwnDropdownV2 } from '@/ui/OwnDropdown/v2'

interface Props {
  /** List of options to display */
  options: OwnDropdownItem[]
  /** Label for the "all" option */
  allLabel?: string
  /** Placeholder text when nothing is selected */
  placeholder?: string
  /** Whether the dropdown should be open by default */
  defaultOpen?: boolean
}

const modelValue = defineModel<string | undefined>({ default: undefined })

const props = withDefaults(defineProps<Props>(), {
  allLabel: 'All',
  defaultOpen: false,
  placeholder: 'Select...',
})

// State for dropdown functionality
const isOpen = ref(props.defaultOpen)

// Add "All" option to the beginning of options
const allOptions = computed(() => [
  {
    label: props.allLabel,
    value: '',
  },
  ...props.options,
])

// Get the label for the selected value
const selectedLabel = computed(() => {
  if (!modelValue.value) {
    return props.allLabel
  }
  const option = props.options.find((opt) => opt.value === modelValue.value)
  return option?.label || props.placeholder
})

// Handle option selection
const handleSelect = (item: OwnDropdownItem) => {
  const value = typeof item === 'string' ? item : item.value
  modelValue.value = value || undefined
  isOpen.value = false
}

// Handle dropdown open/close
const handleOpenChange = (open: boolean) => {
  isOpen.value = open
}
</script>

<template>
  <OwnDropdownV2 :open="isOpen" @update:open="handleOpenChange">
    <template #trigger>
      <DropdownMenuTrigger as-child>
        <OwnButton
          variant="secondary"
          :text="selectedLabel"
          small
          class="min-w-[140px]"
        >
          <template #icon-right>
            <PhCaretDown
              :size="16"
              weight="bold"
              :class="[
                'transition-transform duration-200',
                isOpen && 'rotate-180',
              ]"
            />
          </template>
        </OwnButton>
      </DropdownMenuTrigger>
    </template>

    <template #menu>
      <OwnDropdownMenuV2
        :items="allOptions"
        :active="modelValue || ''"
        :visible-options="6.5"
        @selected="handleSelect"
      />
    </template>
  </OwnDropdownV2>
</template>
